import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { getApiRoute } from "src/services";
import {
  getRedirectResult,
  connectAuthEmulator,
  isSignInWithEmailLink,
  signInWithEmailLink,
  checkActionCode,
} from "firebase/auth";
import { auth } from "../firebase/firebase.js";
import { useSnackbarContext } from "src/utils/SnackbarContext.js";
import { customEvent } from "./gtag.js";
import { capitalize } from "lodash";
import { rollbar } from "./ErrorBoundary/index.js";
import { injectHotjar } from "./hotjar.js";
import { isEmbeddedPath } from "./Utils.js";

export default function useAuth({ isEmbedded }) {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const {
    setSnackbarOpen,
    setSnackbarMessage,
    setSnackbarSeverity,
    showSnackBar,
  } = useSnackbarContext();
  const searchParams = new URLSearchParams(window.location.search);
  const email = searchParams.get("email");
  const hasSignedInRef = useRef({ status: false, providerName: null });

  async function getAuth({ isRefetching = false } = { isRefetching: false }) {
    try {
      const response = await axios.get(getApiRoute("auth", "GET_AUTH"), {
        withCredentials: true,
      });
      setUser(response.data);
      if (!isRefetching) {
        hasSignedInRef.current.status = true;
        hasSignedInRef.current.providerName = response.data.provider;
      }
      return response.data;
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  }

  async function refetchAuth() {
    return await getAuth({ isRefetching: true });
  }

  useEffect(() => {
    getAuth().then(() => {
      if (hasSignedInRef.current.status === false) {
        checkSignIn(true, null);
      }
    });
  }, []);

  useEffect(() => {
    if (hasSignedInRef.current.status && user) {
      if (!isEmbedded && !isEmbeddedPath(window.location)) {
        injectHotjar();
        customEvent({
          name: `user_signed_in`,
          category: "Authorization",
          label: `Sign in with ${capitalize(
            hasSignedInRef.current.providerName
          )} Successfully`,
          data: {
            user_id: user._id,
            user_email: user.email,
            user_handle: user.handle ?? "no_handle",
            provider: hasSignedInRef.current.providerName,
          },
        });
        if (!user.hasValidSubscription) {
          customEvent({
            name: `user_signed_in_free`,
            category: "Authorization",
            label: `Sign in with ${capitalize(
              hasSignedInRef.current.providerName
            )} Successfully`,
            data: {
              user_id: user._id,
              user_email: user.email,
              user_handle: user.handle ?? "no_handle",
              provider: hasSignedInRef.current.providerName,
            },
          });

          customEvent({
            name: `free_user_${user.addedCalendars?.length}`,
            category: "Authorization",
            label: `Sign in with ${capitalize(
              hasSignedInRef.current.providerName
            )} Successfully`,
            data: {
              user_id: user._id,
              user_email: user.email,
              user_handle: user.handle ?? "no_handle",
              provider: hasSignedInRef.current.providerName,
            },
          });
        }
      }
      if (!user.hasValidSubscription && user.stripeCustomerId) {
        customEvent({
          name: `user_signed_in_stripe_customer`,
          category: "Authorization",
          label: `Sign in with ${capitalize(
            hasSignedInRef.current.providerName
          )} Successfully`,
          data: {
            user_id: user._id,
            user_email: user.email,
            user_handle: user.handle ?? "no_handle",
            provider: hasSignedInRef.current.providerName,
          },
        });
      }
      hasSignedInRef.current.status = false;
    }
  }, [isEmbedded, user]);

  const handleSignOut = async () => {
    try {
      await axios.post(getApiRoute("auth", "POST_SIGNOUT"), null, {
        withCredentials: true,
      });
      setUser(null);
    } catch (error) {
      console.error(error);
    } finally {
      setUser(null);
    }
  };

  useEffect(() => {
    const host = process.env.REACT_APP_FIREBASE_AUTH_EMULATOR_HOST;
    if (host) {
      connectAuthEmulator(auth, host);
    }
  }, []);

  const checkSignIn = async (withRedirect, resultFromPopup) => {
    try {
      let result = resultFromPopup;
      let actionCodeInfo = "";
      if (isSignInWithEmailLink(auth, window.location.href) && email) {
        try {
          const oobCode = searchParams.get("oobCode");
          await checkActionCode(auth, oobCode);
          result = await signInWithEmailLink(auth, email);
        } catch (err) {
          console.error(err);
          /*
          rollbar.error(err, {
            email,
            url: window.location.href,
            error_message: err.message,
            error_code: err.code,
            description: "Sign in failed on signInWithEmailLink",
          });
          */
          if (err.code === "auth/expired-action-code") {
            return showSnackBar(
              setSnackbarOpen,
              setSnackbarMessage,
              setSnackbarSeverity,
              "The sign-in link has expired. Please request a new one.",
              "error"
            );
          } else if (err.code === "auth/invalid-action-code") {
            return showSnackBar(
              setSnackbarOpen,
              setSnackbarMessage,
              setSnackbarSeverity,
              "The sign-in link is invalid. It may have already been used.",
              "error"
            );
          } else if (err.code === "auth/invalid-email") {
            return showSnackBar(
              setSnackbarOpen,
              setSnackbarMessage,
              setSnackbarSeverity,
              "Your email is incompatible. Please try with another one.",
              "error"
            );
          }
          // else auth/network-request-failed or auth/too-many-requests (network errors)
          return showSnackBar(
            setSnackbarOpen,
            setSnackbarMessage,
            setSnackbarSeverity,
            "There was a network error. Please try again sending a new link.",
            "error"
          );
        }
      } else if (withRedirect) {
        result = await getRedirectResult(auth);
      }

      if (result) {
        let providerName;
        let idToken;

        // Determine the provider and get token
        if (result.providerId === "google.com") {
          providerName = "google";
          idToken = await result.user.getIdToken();
        } else if (result.providerId === "apple.com") {
          providerName = "apple";
          const tokenResult = await result.user.getIdTokenResult(true);
          idToken = tokenResult.token;
        } else if (result.providerId === "microsoft.com") {
          providerName = "microsoft";
          idToken = await result.user.getIdToken();
        } else if (
          result.providerId === "email" ||
          result.providerId === null
        ) {
          // It's the email link provider
          providerName = "email";
          idToken = await result.user.getIdToken();
        }

        // If provider is recognized, continue with sign in
        if (providerName && idToken) {
          const response = await axios.post(
            getApiRoute("auth", "POST_SIGNIN"),
            { idToken, provider: providerName },
            { withCredentials: true }
          );
          if (response.status === 200) {
            hasSignedInRef.current.status = true;
            hasSignedInRef.current.providerName = providerName;
            getAuth();
            showSnackBar(
              setSnackbarOpen,
              setSnackbarMessage,
              setSnackbarSeverity,
              "Signing you in...",
              "success"
            );
          } else {
            showSnackBar(
              setSnackbarOpen,
              setSnackbarMessage,
              setSnackbarSeverity,
              "Sign in failed. Please try again.",
              "error"
            );
          }
        } else {
          showSnackBar(
            setSnackbarOpen,
            setSnackbarMessage,
            setSnackbarSeverity,
            "Sign in failed. Please try again.",
            "error"
          );
        }
      }
    } catch (err) {
      // Handle errors
      console.log(err);
      rollbar.error(err, {
        email,
        url: window.location.href,
        error_message: err.message,
        error_code: err.code,
        description: "Sign in failed on checkSignIn",
      });
      let errorMessage = err?.response?.data?.message;
      showSnackBar(
        setSnackbarOpen,
        setSnackbarMessage,
        setSnackbarSeverity,
        errorMessage ?? "Sign in failed. Please try again.",
        "error"
      );
    }
  };

  return { user, isLoading, handleSignOut, refetchAuth, checkSignIn };
}
